<script setup>
import { i18n } from 'boot/i18n';
import { ServiceDeliveryFields } from 'src/contexts/treatmentPlanning';
import {
  HrConfirmBtn,
  HrDismissBtn,
  HrFormDialog,
  HrInfoBanner,
  HrP,
  HrSettingCheckbox,
  notifyNegative,
  notifySuccess,
} from 'src/design-system';
import { formatCalendarDate } from 'src/utils/DateUtils';
import { computed, ref } from 'vue';

import { FIX_SECTION } from '../../domain/services/holdReasonFix';
import { useSessionPlanDeliveryFix } from '../composables/useSessionPlanDeliveryFix';

/**
 * Adds a missing service or place of service without opening the session: to the one session a
 * biller picked in billing items, or to every session a plan ran, from the plan's task.
 *
 * THE PLAN IS OPT-IN AND EXPLAINED, AND ONLY FOR THE GROUP. A session plan is part of the client's
 * treatment plan, which is clinical work; a biller fixing last week's sessions is not necessarily the
 * person who should change it. So it is a separate, unchecked choice, offered only to
 * somebody the plan's own rule would let change it. A single session never offers it: fixing one row
 * is usually the exception to what the plan says.
 */
defineOptions({ name: 'FixSessionPlanDeliveryDialog' });

const props = defineProps({
  clinicId: {
    type: String,
    required: true,
  },
  /** Which half is missing, as the fix names it. */
  section: {
    type: String,
    required: true,
  },
  /** The sessions and the plan, supplied by the host because billing owns neither: `SessionDeliverySurfaces`. */
  sessionDelivery: {
    type: Object,
    required: true,
  },
  /** For a plan's task: `<plan id>:<activity type>`. Give this or `sessionActivityId`. */
  sessionPlanDeclaration: {
    type: String,
    default: null,
  },
  /** For one row in billing items: the session to fix. */
  sessionActivityId: {
    type: String,
    default: null,
  },
});

const emit = defineEmits({
  ok: null,
});

const KEY = 'billingItems.holdTask.deliveryFix';

const dialog = ref(null);
const alsoOnPlan = ref(false);
const applying = ref(false);
const fix = useSessionPlanDeliveryFix(props);

const missing = computed(() => (props.section === FIX_SECTION.PLACE_OF_SERVICE
  ? 'PlaceOfService'
  : 'TreatmentService'));
const planName = computed(() => fix.plan.value?.name ?? i18n.t(`${KEY}.textThisPlan`));
const count = computed(() => fix.sessionCount.value);

// A plan declares supervision apart from its sessions, so a supervision group says so.
const ofSupervision = computed(() => (fix.activityType === 'supervision' ? 'Supervision' : ''));

const lead = computed(() => (fix.isPlan
  ? i18n.tc(`${KEY}.textPlan${ofSupervision.value}${missing.value}`, count.value, {
    count: count.value, plan: planName.value,
  })
  : i18n.t(`${KEY}.textOne${missing.value}`)));

/** When the sessions were delivered, as one date or a range, worded for how many there are. */
const dates = computed(() => {
  const all = fix.serviceDates.value;

  if (all.length === 0) {
    return null;
  }
  if (all.length > 1) {
    return i18n.t(`${KEY}.textDeliveredBetween`, {
      from: formatCalendarDate(all[0]), to: formatCalendarDate(all[all.length - 1]),
    });
  }
  const date = formatCalendarDate(all[0]);

  if (count.value === 1) {
    return i18n.t(`${KEY}.textDeliveredOn`, { date });
  }
  return count.value === 2
    ? i18n.t(`${KEY}.textBothDeliveredOn`, { date })
    : i18n.t(`${KEY}.textAllDeliveredOn`, { count: count.value, date });
});

/** For a group only: that a session keeps what it already has. */
const note = computed(() => (fix.isPlan && !fix.reading.value && count.value > 0
  ? i18n.t(`${KEY}.textKeepsExisting`)
  : null));

function show() {
  dialog.value?.show();
} // show()

function hide() {
  dialog.value?.hide();
} // hide()

function reportOutcome({ fixed, failed, plan }) {
  if (fixed > 0) {
    notifySuccess(i18n.tc(`${KEY}.notifyFixed`, fixed, { count: fixed }));
  }
  if (failed.length > 0) {
    notifyNegative(i18n.tc(`${KEY}.notifySomeFailed`, failed.length, { count: failed.length }));
  }
  if (plan === 'failed') {
    notifyNegative(i18n.t(fixed > 0 ? `${KEY}.notifyPlanFailed` : `${KEY}.notifyPlanFailedOnly`));
  }
} // reportOutcome()

async function onSubmit() {
  if (!fix.canApply.value || applying.value) {
    return;
  }
  applying.value = true;
  try {
    const outcome = await fix.apply(fix.canEditPlan.value && alsoOnPlan.value);

    reportOutcome(outcome);
    emit('ok', outcome);
    hide();
  } catch (error) {
    console.error('Failed to add the missing service delivery', error);
    notifyNegative(i18n.t(`${KEY}.notifyFailed`));
  } finally {
    applying.value = false;
  }
} // onSubmit()

defineExpose({ show, hide });
</script>

<template>
  <hr-form-dialog
    ref="dialog"
    size="md"
    :title="i18n.t(`${KEY}.title${missing}`)"
    data-e2e="dialog-fix-session-delivery"
    @submit="onSubmit"
  >
    <div class="row q-col-gutter-md">
      <hr-info-banner
        v-if="fix.failedToRead.value"
        class="col-12"
        :message="i18n.t(`${KEY}.textReadFailed`)"
      />
      <template v-else>
        <hr-p
          class="col-12"
          :pending="fix.reading.value"
          :text="count > 0 ? [lead, dates].filter(Boolean).join(' ') : i18n.t(`${KEY}.textNothingLeft`)"
        />
        <service-delivery-fields
          v-model="fix.chosen.value"
          :places-of-service="fix.placesOfService.value"
          :treatment-services="fix.treatmentServices.value"
          :is-loading="fix.isLoading.value"
          :disable="fix.reading.value || count === 0"
        />
        <hr-p
          v-if="note"
          class="col-12"
          :text="note"
        />
        <template v-if="fix.isPlan && !fix.reading.value && count > 0">
          <hr-setting-checkbox
            v-if="fix.canEditPlan.value"
            v-model="alsoOnPlan"
            class="col-12"
            :label="i18n.t(`${KEY}.labelAlsoOnPlan${ofSupervision}`)"
            :description="i18n.t(`${KEY}.descriptionAlsoOnPlan`, { plan: planName })"
            data-e2e="checkbox-fix-also-on-session-plan"
          />
          <hr-info-banner
            v-else
            class="col-12"
            :message="i18n.t(`${KEY}.textAskPlanEditor`)"
          />
        </template>
      </template>
    </div>

    <template #actions>
      <hr-dismiss-btn
        :label="i18n.t('common.buttonCancel')"
        @click="hide"
      />
      <hr-confirm-btn
        :label="fix.isPlan ? i18n.tc(`${KEY}.buttonUpdate`, count, { count }) : i18n.t(`${KEY}.buttonUpdateOne`)"
        type="submit"
        :loading="applying"
        :disable="!fix.canApply.value"
        data-e2e="button-fix-session-delivery"
      />
    </template>
  </hr-form-dialog>
</template>
